import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  AbyssViewerComponentProps,
  AbyssViewerContextProvider,
  AbyssViewerNoContext,
  ColorMap,
  getCurrentTransformMatrix,
  Number3,
  PointCloudMaterialProps,
  PointPickResult,
  positionRotationScaleFromMatrix4,
  rotationCenterFromWorldPoint,
  Transform,
  TransformControls,
  TransformSpace,
  TransformTarget,
  useAbyssViewerContext,
  viewerRotationFromAbyssRotation,
} from '@fabric-web/3d-viewer';
import * as analysisState from '@/components/Analysis/state';
import * as state from '../state';
import * as globalState from '@/state';
import { GetStructureForCadAlignmentQuery } from '@/__generated__/graphql';
import { useViewerPerformanceAlert } from '@/hooks/useViewerPerformanceAlert';
import { getCloudfrontUrl } from '@/utils/cloudfront';
import { PageLoader } from '@/components/shared/PageLoader';
import { usePointCloudInfo } from '@/hooks/usePointCloudInfo';
import { useScansTransform } from '../hooks/useScansTransform';
import { transformModeToString } from '../util';

interface ViewerProps {
  structureId: string;
  structureData: GetStructureForCadAlignmentQuery;
  performanceAlert?: false | JSX.Element;
}

const canvasProps = {
  style: {
    background: '#263238',
    width: '100%',
    // height: '100%',
    height: '90vh',
  },
  id: 'outer-viewer',
  gl: { antialias: true, preserveDrawingBuffer: true },
};

interface ViewerInnerProps extends ViewerProps {
  structureId: string;
  structureData: GetStructureForCadAlignmentQuery;
  performanceAlert?: false | JSX.Element;
}

const ViewerInner = ({ structureData, performanceAlert }: ViewerInnerProps) => {
  const { callbacks, getTransformTarget } = useAbyssViewerContext();
  const cameraTarget = useAtomValue(analysisState.cameraTarget);
  const [visibilityBox, setVisibilityBox] = useAtom(globalState.currentVisibilityBox);
  const isScansVisible = useAtomValue(state.isScansVisible);
  const isCadVisible = useAtomValue(state.isCadVisible);
  const enableAdaptivePointBudget = useAtomValue(globalState.enableAdaptivePointBudget);
  const pointBudget = useAtomValue(globalState.pointBudget);
  const pointSize = useAtomValue(globalState.pointSize);
  const enableDynamicPointSize = useAtomValue(globalState.enableDynamicPointSize);
  const edlStrength = useAtomValue(globalState.edlStrength);
  const [isInitialPointCloudLoaded, setIsInitialPointCloudLoaded] = useState(false);
  const setSceneBoundingBox = useSetAtom(state.sceneBoundingBox);
  const { maxIds: scansMaxIds, onOctreeHierarchyLoaded: onScansOctreeHierarchyLoaded } =
    usePointCloudInfo();
  const { maxIds: cadMaxIds, onOctreeHierarchyLoaded: onCadOctreeHierarchyLoaded } =
    usePointCloudInfo();

  const { scansTransform, setScansTransform } = useScansTransform();

  const [rotationCenter, setRotationCenter] = useAtom(state.rotationCenter);
  const [isWaitingRotationCenter, setIsWaitingRotationCenter] = useAtom(
    state.isWaitingForRotationCenter
  );

  const transformMode = useAtomValue(state.transformMode);
  const activeAxes = useAtomValue(state.activeAxes);
  const [transformTarget, setTransformTarget] = useState<TransformTarget>();

  const isTransformEnabled = transformMode !== undefined;

  const [fixedScansTransform, setFixedScansTransform] = useState<Transform>();
  useEffect(() => {
    // we shouldn't change scans position and rotation when transform controls are active.
    if (!isTransformEnabled) {
      setFixedScansTransform(scansTransform);
    }
  }, [isTransformEnabled, scansTransform]);

  useEffect(() => {
    if (!scansTransform && structureData.structure) {
      const { scansToCadTransform } = structureData.structure;
      if (scansToCadTransform && scansToCadTransform.length === 6) {
        const position: Number3 = scansToCadTransform.slice(0, 3) as Number3;
        const abyssRotation: Number3 = scansToCadTransform.slice(3, 6) as Number3;
        const rotation: Number3 = viewerRotationFromAbyssRotation(abyssRotation);

        setScansTransform({
          position,
          rotation,
          scale: [1, 1, 1],
        });
      } else {
        setScansTransform({
          position: [0, 0, 0],
          rotation: [0, 0, 0],
          scale: [1, 1, 1],
        });
      }
    }
  }, [structureData, scansTransform, setScansTransform]);

  useEffect(() => {
    if (isTransformEnabled) {
      const target = getTransformTarget('scans-octree');
      if (!target) {
        throw new Error('scans-octree not found');
      }
      setTransformTarget(target);
    } else {
      setTransformTarget(undefined);
    }
  }, [isTransformEnabled, getTransformTarget]);

  const showAxes = useMemo(() => {
    const mode = transformModeToString(transformMode);
    const axes = (mode && activeAxes[mode]) ?? '';
    return {
      showX: axes.includes('X'),
      showY: axes.includes('Y'),
      showZ: axes.includes('Z'),
    };
  }, [transformMode, activeAxes]);

  const onChangeTransform = useCallback(() => {
    // console.log('onChangeTransform');
    if (!transformTarget) return;
    const matrix4 = getCurrentTransformMatrix(transformTarget);
    const scansTransform = positionRotationScaleFromMatrix4(matrix4);
    setScansTransform(scansTransform);
  }, [transformTarget, setScansTransform]);

  const onPointCloudClick = useCallback(
    (point: PointPickResult) => {
      const clickPosition = point.getPointAttribute(point.pointIndex, 'position') as Number3;
      // console.log('onPointCloudClick', clickPosition);
      if (isWaitingRotationCenter) {
        const target = getTransformTarget('scans-octree');
        if (!target) return;
        const newRotationCenter = rotationCenterFromWorldPoint(target, clickPosition);
        setRotationCenter(newRotationCenter);
        setIsWaitingRotationCenter(false);
        // eslint-disable-next-line no-console
        console.log('NEW rotation center', newRotationCenter);
      }
    },
    [isWaitingRotationCenter, getTransformTarget, setRotationCenter, setIsWaitingRotationCenter]
  );

  useEffect(() => {
    if (isInitialPointCloudLoaded) {
      const sceneBbox = callbacks?.getSceneBoundingBox?.();
      setSceneBoundingBox(sceneBbox);
    }
  }, [isInitialPointCloudLoaded, setSceneBoundingBox, callbacks]);

  const octreePaths = useMemo(() => {
    const scansOctreePath =
      (structureData.structure && getCloudfrontUrl(structureData.structure?.octreeResourcesPath)) ??
      '';
    const cadOctreePath =
      (structureData.structure?.cadOctreeResourcesPath &&
        getCloudfrontUrl(structureData.structure?.cadOctreeResourcesPath)) ??
      '';
    return { scansOctreePath, cadOctreePath };
  }, [structureData]);

  const scansVisibilityMap = useRef(new ColorMap(16, undefined, 0x112233ff));
  const cadVisibilityMap = useRef(new ColorMap(16, undefined, 0x112233ff));

  useEffect(() => {
    const color = isScansVisible ? 0x112233ff : 0x11223300;
    scansVisibilityMap.current.clear(color);
  }, [isScansVisible]);

  useEffect(() => {
    const color = isCadVisible ? 0x112233ff : 0x11223300;
    cadVisibilityMap.current.clear(color);
  }, [isCadVisible]);

  const scansMaterial: PointCloudMaterialProps = useMemo(() => {
    const maxId = scansMaxIds?.['annotation'] ?? 1024;
    const colorMap = new ColorMap(maxId, undefined, 0xff3300ff);

    const material: PointCloudMaterialProps = {
      colorMap,
      visibilityMap: scansVisibilityMap.current,
      attributeName: 'annotation',
      visibilityAttributeName: 'annotation',
      hasColor: false,
      enableSizeAttenuation: enableDynamicPointSize,
      pointSize,
      allowInconsistentMaxId: true,
    };

    return material;
  }, [scansMaxIds, pointSize, enableDynamicPointSize, scansVisibilityMap]);

  const cadMaterial: PointCloudMaterialProps = useMemo(() => {
    const maxId = cadMaxIds?.['id'] ?? 1024;
    const colorMap = new ColorMap(maxId, undefined, 0x00ff33ff);

    const material: PointCloudMaterialProps = {
      colorMap,
      visibilityMap: cadVisibilityMap.current,
      attributeName: 'id',
      visibilityAttributeName: 'id',
      hasColor: false,
      enableSizeAttenuation: enableDynamicPointSize,
      pointSize,
      allowInconsistentMaxId: true,
    };

    return material;
  }, [cadMaxIds, enableDynamicPointSize, pointSize, cadVisibilityMap]);

  const pointCloudMaterials = useMemo(() => {
    const materials = new Map<string, PointCloudMaterialProps>();
    materials.set('scansMaterial', scansMaterial);
    materials.set('cadMaterial', cadMaterial);
    return materials;
  }, [scansMaterial, cadMaterial]);

  const pointClouds = useMemo(
    () => [
      {
        name: 'Scans',
        id: 'scans-octree',
        url: octreePaths.scansOctreePath,
        material: 'scansMaterial',
        onOctreeHierarchyLoaded: onScansOctreeHierarchyLoaded,
        ...fixedScansTransform,
        rotationCenter,
      },
      {
        name: 'CAD',
        id: 'cad-octree',
        url: octreePaths.cadOctreePath,
        material: 'cadMaterial',
        onOctreeHierarchyLoaded: onCadOctreeHierarchyLoaded,
      },
    ],
    [
      octreePaths,
      onScansOctreeHierarchyLoaded,
      onCadOctreeHierarchyLoaded,
      fixedScansTransform,
      rotationCenter,
    ]
  );

  const centerOnLoad = useCallback(() => {
    setIsInitialPointCloudLoaded(true);
    if (!cameraTarget && callbacks?.resetCameraPositionToDefault) {
      callbacks.resetCameraPositionToDefault();
    }
  }, [setIsInitialPointCloudLoaded, cameraTarget, callbacks]);

  const abyssViewerComponentProps: AbyssViewerComponentProps = useMemo(
    () => ({
      // selectionBoxIsEnabled: false,
      onInitialPointCloudLoad: centerOnLoad,
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      enableAdaptivePointBudget,
      pointBudget,
      edlStrength,
      // northRotation: inspectionMetadata?.config?.northRotation || undefined,
      enableEDL: !!edlStrength,
      // enableEDL: false,

      // models,
      visibilityBox,
      setVisibilityBox,
      viewCube: true,
      // floorLevels,
      // selectionColors,
      // showPointCloudsInSphericalView: true,
      // imageSets,
      // sphericalImageSets,
      // sphericalImageVisibilityRange: SPHERICAL_IMAGE_VISIBILITY_RANGE,
      fileLoaderOptions: { withCredentials: true },
      enableLoaderRetry: true,
      // polygons,
      // markers,
      // svgMarkerSets,
      // measurementLines,
      // onPointCloudSelection,
      onPointCloudClick,
      // currentImage: currentSpherical,
      // setCurrentImage: setCurrentSpherical,
      // nextCurrentImage: nextCurrentSpherical,
      // setNextCurrentImage: setNextCurrentSpherical,
      // onSphericalViewClick,
      // unitSystem: viewerUnitSystem,
      // onFileLoadError,
      // imageBrightness: brightness,
      // imageContrast: contrast,
      // htmlMarkerSets,
      // smallVisibilityBox,
      // setSmallVisibilityBox,
    }),
    [
      centerOnLoad,
      cameraTarget,
      pointCloudMaterials,
      pointClouds,
      enableAdaptivePointBudget,
      pointBudget,
      edlStrength,
      // models,
      // inspectionMetadata?.config?.northRotation,
      visibilityBox,
      setVisibilityBox,
      // viewCube,
      // floorLevels,
      // selectionColors,
      // imageSets,
      // sphericalImageSets,
      // polygons,
      // markers,
      // svgMarkerSets,
      // measurementLines,
      // onPointCloudSelection,
      onPointCloudClick,
      // currentSpherical,
      // setCurrentSpherical,
      // nextCurrentSpherical,
      // setNextCurrentSpherical,
      // onSphericalViewClick,
      // viewerUnitSystem,
      // onFileLoadError,
      // brightness,
      // contrast,
      // htmlMarkerSets,
      // smallVisibilityBox,
      // setSmallVisibilityBox,
    ]
  );

  const overlayNode = useMemo(() => {
    return (
      <>
        {!isInitialPointCloudLoaded && <PageLoader background="dark" />}
        {performanceAlert}
      </>
    );
  }, [
    // assemblyLoading,
    // inactiveMarkingFlow,
    isInitialPointCloudLoaded,
    // loadErrorAlert,
    performanceAlert,
    // selectedSpherical,
    // structureRelationship.loaded,
    // updateCameraTarget,
    // screenshotMode,
  ]);

  return (
    <AbyssViewerNoContext
      abyssViewerComponentProps={abyssViewerComponentProps}
      overlayNode={overlayNode}
    >
      {transformMode !== undefined && (
        <TransformControls
          mode={transformMode}
          // space={TransformSpace.World}
          space={TransformSpace.Local}
          size={2}
          target={transformTarget}
          onChange={onChangeTransform}
          {...showAxes}
        />
      )}
    </AbyssViewerNoContext>
  );
};

export const Viewer = (props: ViewerProps) => {
  const { performanceAlert, onMajorPerformanceCaveat } = useViewerPerformanceAlert();
  return (
    <AbyssViewerContextProvider
      canvasProps={canvasProps}
      onMajorPerformanceCaveat={onMajorPerformanceCaveat}
    >
      <ViewerInner {...props} performanceAlert={performanceAlert}>
        {/* {children} */}
      </ViewerInner>
    </AbyssViewerContextProvider>
  );
};
